export type CalendarEvent = {
  uid: string;
  sequence: number;
  date: string;
  time: string;
  timeZone: string;
  summary: string;
  location: string;
  description: string;
  canceled?: boolean;
};

const pad = (value: number) => String(value).padStart(2, "0");

const icsEscape = (value: unknown) => String(value ?? "")
  .replaceAll("\\", "\\\\")
  .replaceAll("\n", "\\n")
  .replaceAll(",", "\\,")
  .replaceAll(";", "\\;");

export function isValidTimeZone(timeZone: string) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone }).format();
    return Boolean(timeZone);
  } catch {
    return false;
  }
}

export function zonedDateTimeToUtc(date: string, time: string, timeZone: string) {
  if (!isValidTimeZone(timeZone)) throw new Error(`Invalid time zone: ${timeZone}`);
  const [year, month, day] = date.split("-").map(Number);
  const [hour, minute] = time.slice(0, 5).split(":").map(Number);
  const targetWallTime = Date.UTC(year, month - 1, day, hour, minute);
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  let instant = targetWallTime;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = Object.fromEntries(formatter.formatToParts(new Date(instant)).map((part) => [part.type, part.value]));
    const representedWallTime = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
    );
    const correction = targetWallTime - representedWallTime;
    instant += correction;
    if (correction === 0) break;
  }
  return new Date(instant);
}

function calendarUtc(value: Date) {
  return `${value.getUTCFullYear()}${pad(value.getUTCMonth() + 1)}${pad(value.getUTCDate())}T${pad(value.getUTCHours())}${pad(value.getUTCMinutes())}${pad(value.getUTCSeconds())}Z`;
}

export function buildCalendarEvent(event: CalendarEvent, now = new Date()) {
  const start = zonedDateTimeToUtc(event.date, event.time, event.timeZone);
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  const method = event.canceled ? "CANCEL" : "REQUEST";
  const status = event.canceled ? "CANCELLED" : "CONFIRMED";
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//NextTable//Date//EN",
    `METHOD:${method}`,
    "CALSCALE:GREGORIAN",
    `X-WR-TIMEZONE:${icsEscape(event.timeZone)}`,
    "BEGIN:VEVENT",
    `UID:${event.uid}@nexttable.net`,
    `SEQUENCE:${event.sequence}`,
    `DTSTAMP:${calendarUtc(now)}`,
    `DTSTART:${calendarUtc(start)}`,
    `DTEND:${calendarUtc(end)}`,
    `STATUS:${status}`,
    `SUMMARY:${icsEscape(event.summary)}`,
    `LOCATION:${icsEscape(event.location)}`,
    `DESCRIPTION:${icsEscape(event.description)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}
